import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import PrivateImage from '../../components/common/PrivateImage';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Save,
  UserCircle,
  Trophy,
  AlertCircle,
  Users
} from 'lucide-react';

const MatchCallup = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [callup, setCallup] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Cargar partido
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('*')
        .eq('id', id)
        .single();

      if (matchError) throw matchError;

      if (!matchData) {
        navigate('/matches');
        return;
      }

      setMatch(matchData);

      // Cargar jugadores del equipo
      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('*')
        .eq('team', matchData.team)
        .eq('active', true)
        .order('last_name', { ascending: true });

      if (playersError) throw playersError;

      setPlayers(playersData || []);

      // Cargar convocatoria existente
      const { data: callupData, error: callupError } = await supabase
        .from('match_callup')
        .select('*')
        .eq('match_id', id);

      if (callupError) throw callupError;

      const callupMap = {};
      (callupData || []).forEach(call => {
        callupMap[call.player_id] = {
          is_called: call.is_called,
          absence_reason: call.absence_reason || '',
          id: call.id
        };
      });

      setCallup(callupMap);

    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCallupChange = (playerId, isCalled) => {
    setCallup(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        is_called: isCalled,
        absence_reason: isCalled ? '' : prev[playerId]?.absence_reason || ''
      }
    }));
  };

  const handleReasonChange = (playerId, reason) => {
    setCallup(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        absence_reason: reason
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const callupRecords = Object.entries(callup)
        .filter(([_, data]) => data.is_called !== undefined)
        .map(([playerId, data]) => ({
          match_id: parseInt(id),
          player_id: parseInt(playerId),
          is_called: data.is_called,
          absence_reason: !data.is_called ? data.absence_reason || null : null
        }));

      if (callupRecords.length === 0) {
        throw new Error('Debes marcar al menos un jugador');
      }

      // Eliminar convocatoria anterior
      await supabase
        .from('match_callup')
        .delete()
        .eq('match_id', id);

      // Insertar nueva convocatoria
      const { error: insertError } = await supabase
        .from('match_callup')
        .insert(callupRecords);

      if (insertError) throw insertError;

      setSuccess(true);

      setTimeout(() => {
        navigate('/matches');
      }, 2000);

    } catch (err) {
      console.error('Error al guardar convocatoria:', err);
      setError(err.message || 'Error al guardar la convocatoria');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const getStats = () => {
    const stats = {
      total: players.length,
      called: 0,
      notCalled: 0,
      pending: 0
    };

    players.forEach(player => {
      const playerCallup = callup[player.id];
      if (playerCallup?.is_called === true) {
        stats.called++;
      } else if (playerCallup?.is_called === false) {
        stats.notCalled++;
      } else {
        stats.pending++;
      }
    });

    return stats;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Convocatoria guardada!</h2>
          <p className="text-gray-600 mb-4">La convocatoria se ha registrado correctamente</p>
          <button
            onClick={() => navigate('/matches')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver a Partidos
          </button>
        </div>
      </div>
    );
  }

  const stats = getStats();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/matches')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Convocatoria</h1>
                {match && (
                  <p className="text-sm text-gray-600">
                    {match.team} vs {match.opponent} - {formatDate(match.date)} ({formatTime(match.start_time)})
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving || stats.pending === stats.total}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar Convocatoria'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Estadísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-600">Total jugadores</p>
          </div>
          <div className="bg-green-50 rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.called}</p>
            <p className="text-sm text-green-700">Convocados</p>
          </div>
          <div className="bg-red-50 rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.notCalled}</p>
            <p className="text-sm text-red-700">No convocados</p>
          </div>
          <div className="bg-gray-50 rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-gray-600">{stats.pending}</p>
            <p className="text-sm text-gray-700">Pendientes</p>
          </div>
        </div>

        {/* Lista de jugadores */}
        {players.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <UserCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay jugadores</h3>
            <p className="text-gray-600">No hay jugadores registrados en este equipo</p>
          </div>
        ) : (
          <div className="space-y-4">
            {players.map((player) => {
              const playerCallup = callup[player.id] || {};
              const isCalled = playerCallup.is_called;

              return (
                <div
                  key={player.id}
                  className={`bg-white rounded-xl shadow-sm p-6 transition-all ${
                    isCalled === true ? 'bg-green-50 border-2 border-green-200' :
                    isCalled === false ? 'bg-red-50 border-2 border-red-200' : ''
                  }`}
                >
                  <div className="flex items-start gap-6">
                    {/* Foto del jugador */}
                    <div className="flex-shrink-0">
                      {player.photo_url ? (
                        <PrivateImage
                          bucket="player-photos"
                          path={player.photo_url}
                          alt={`${player.first_name} ${player.last_name}`}
                          className="w-16 h-16 rounded-full object-cover"
                          fallbackIcon={UserCircle}
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <UserCircle className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      {player.player_number && (
                        <div className="mt-1 text-center">
                          <span className="inline-block px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded">
                            #{player.player_number}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {player.first_name} {player.last_name}
                      </h3>
                      {player.position && (
                        <p className="text-sm text-gray-600 mb-3">{player.position}</p>
                      )}

                      {/* Botones de convocatoria */}
                      <div className="flex flex-wrap gap-3 mb-3">
                        <button
                          onClick={() => handleCallupChange(player.id, true)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            isCalled === true
                              ? 'bg-green-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Convocar
                        </button>
                        <button
                          onClick={() => handleCallupChange(player.id, false)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            isCalled === false
                              ? 'bg-red-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <XCircle className="w-4 h-4" />
                          No convocar
                        </button>
                      </div>

                      {/* Motivo de ausencia si no está convocado */}
                      {isCalled === false && (
                        <div className="bg-white border border-red-200 rounded-lg p-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Motivo de no convocatoria
                          </label>
                          <input
                            type="text"
                            placeholder="Ej: Lesión, sancionado, motivos personales..."
                            value={playerCallup.absence_reason || ''}
                            onChange={(e) => handleReasonChange(player.id, e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchCallup;
